import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './common';

export const CadenceTriggerKindSchema = z.enum(['payment-status']);
export type CadenceTriggerKind = z.infer<typeof CadenceTriggerKindSchema>;

export const PaymentStatusSchema = z.enum([
  'pending',
  'paid',
  'expired',
  'failed',
  'cancelled',
  'refunded',
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const CadenceTriggerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('payment-status'),
    status: PaymentStatusSchema,
    gatewayEventName: z.string().min(1).optional(),
  }),
]);
export type CadenceTrigger = z.infer<typeof CadenceTriggerSchema>;

export const CadenceLayerSchema = z.object({
  id: UuidSchema,
  order: z.number().int().positive(),
  delayMinutes: z.number().int().min(0).max(43200),
  templateId: UuidSchema,
  connectionsIds: z.array(UuidSchema).min(1),
  dailyLimit: z.number().int().positive().optional(),
  autoReplyAgentId: UuidSchema.optional(),
  isPaused: z.boolean(),
});
export type CadenceLayer = z.infer<typeof CadenceLayerSchema>;

export const CadenceStatusSchema = z.enum(['active', 'paused', 'archived']);
export type CadenceStatus = z.infer<typeof CadenceStatusSchema>;

export const CadenceSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  name: z.string().min(1).max(100),
  trigger: CadenceTriggerSchema,
  layers: z.array(CadenceLayerSchema).min(1),
  status: CadenceStatusSchema,
  appliesToTags: z.array(z.string().min(1)).optional(),
  totalRunsCount: z.number().int().nonnegative(),
  recoveredAmount: z.number().nonnegative().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Cadence = z.infer<typeof CadenceSchema>;

export const CadenceRunStatusSchema = z.enum(['in-progress', 'completed', 'cancelled', 'failed']);
export type CadenceRunStatus = z.infer<typeof CadenceRunStatusSchema>;

export const CadenceRunSchema = z.object({
  id: UuidSchema,
  cadenceId: UuidSchema,
  leadId: UuidSchema,
  currentLayerOrder: z.number().int().positive(),
  status: CadenceRunStatusSchema,
  startedAt: TimestampSchema,
  completedAt: TimestampSchema.optional(),
});
export type CadenceRun = z.infer<typeof CadenceRunSchema>;

export const CadencesListResponseSchema = z.object({
  cadences: z.array(CadenceSchema),
  total: z.number().int().nonnegative(),
});
export type CadencesListResponse = z.infer<typeof CadencesListResponseSchema>;

export const CadenceRunsListResponseSchema = z.object({
  runs: z.array(CadenceRunSchema),
  total: z.number().int().nonnegative(),
});
export type CadenceRunsListResponse = z.infer<typeof CadenceRunsListResponseSchema>;

export const CreateCadenceLayerInputSchema = CadenceLayerSchema.omit({ id: true });
export type CreateCadenceLayerInput = z.infer<typeof CreateCadenceLayerInputSchema>;

export const CreateCadenceInputSchema = z.object({
  name: z.string().min(1).max(100),
  trigger: CadenceTriggerSchema,
  layers: z.array(CreateCadenceLayerInputSchema).min(1),
  appliesToTags: z.array(z.string().min(1)).optional(),
});
export type CreateCadenceInput = z.infer<typeof CreateCadenceInputSchema>;

export const UpdateCadenceInputSchema = CreateCadenceInputSchema.partial();
export type UpdateCadenceInput = z.infer<typeof UpdateCadenceInputSchema>;
